import type { HtmlAttributesWithChildren } from '@studio/design-system/props';
import { Icon } from '@studio/design-system/icon';
import type { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { twMerge } from 'cn';
import { Sel, STagNew } from '#~/stag/index.js';

const exhibitsRootPath = '/exhibits';

const normalizePathname = (pathname: string): string => pathname.replace(/\/+$/, '');

/** Column. */
export const Column = ({
  children,
  className,
  ...props
}: HtmlAttributesWithChildren): ReactElement => (
  <div
    className={twMerge('flex min-w-[300px] flex-col gap-16 rounded bg-base-200 p-16', className)}
    {...props}
  >
    {children}
  </div>
);

/** Exhibit wrapper. */
export const Exhibit = ({
  children,
  className,
  title,
  ...props
}: HtmlAttributesWithChildren): ReactElement => {
  const { pathname } = useLocation();
  const showBackToExhibits = normalizePathname(pathname) !== exhibitsRootPath;

  return (
    <div
      className={twMerge('relative flex h-screen flex-col items-center overflow-auto', className)}
      {...props}
    >
      {/* Root manifest should not link to itself. */}
      {showBackToExhibits && (
        <Link
          aria-label='Back to exhibits'
          className='absolute left-16 top-16 flex h-28 items-center gap-6 rounded border border-secondary bg-base-200 px-10 text-sm font-demibold text-muted transition hover:bg-foreground/10 hover:text-foreground'
          to={exhibitsRootPath}
          {...STagNew(Sel.Exhibits, 'back_to_root')}
        >
          <Icon icon='arrow-left' />
          <span>Exhibits</span>
        </Link>
      )}
      <h1 className='my-16'>{title}</h1>
      <hr />
      <div className='p-24'>{children}</div>
    </div>
  );
};
